import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ledger_token";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};
export const setToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
};

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => Promise.reject(error),
);

export const Auth = {
  login: (email, password) =>
    api
      .post("/auth/login", { email, password })
      .then((r) => {
        setToken(r.data.token);
        return r.data;
      }),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => {
    setToken(null);
  },
};

export const Bills = {
  list: (params = {}) => api.get("/bills", { params }).then((r) => r.data),
  get: (id) => api.get(`/bills/${id}`).then((r) => r.data),
  create: (payload) => api.post("/bills", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/bills/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/bills/${id}`).then((r) => r.data),
  addPayment: (id, payload) =>
    api.post(`/bills/${id}/payments`, payload).then((r) => r.data),
  markEmailed: (id) =>
    api.post(`/bills/${id}/mark-emailed`).then((r) => r.data),
  markTexted: (id) =>
    api.post(`/bills/${id}/mark-texted`).then((r) => r.data),
  stats: () => api.get("/bills/stats").then((r) => r.data),
};

export const Allocation = {
  buckets: () => api.get("/allocation/buckets").then((r) => r.data),
  saveBuckets: (buckets) =>
    api.put("/allocation/buckets", buckets).then((r) => r.data),
  reset: () => api.post("/allocation/reset").then((r) => r.data),
  preview: (week_start) =>
    api
      .get("/allocation/preview", { params: week_start ? { week_start } : {} })
      .then((r) => r.data),
};

export const Overheads = {
  list: () => api.get("/overheads").then((r) => r.data),
  create: (payload) => api.post("/overheads", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/overheads/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/overheads/${id}`).then((r) => r.data),
  addPayment: (id, payload) =>
    api.post(`/overheads/${id}/payments`, payload).then((r) => r.data),
  deletePayment: (id, paymentId) =>
    api.delete(`/overheads/${id}/payments/${paymentId}`).then((r) => r.data),
  summary: (week_start) =>
    api
      .get("/overheads/summary", { params: week_start ? { week_start } : {} })
      .then((r) => r.data),
};

export const Series = {
  get: (id) => api.get(`/series/${id}`).then((r) => r.data),
  update: (id, payload) => api.put(`/series/${id}`, payload).then((r) => r.data),
  cancel: (id, scope = "future_unpaid") =>
    api.delete(`/series/${id}`, { params: { scope } }).then((r) => r.data),
};

export const OCR = {
  extract: (payload) =>
    api.post("/ocr/extract", payload).then((r) => r.data),
  paymentDetails: (payload) =>
    api.post("/ocr/payment-details", payload).then((r) => r.data),
  smartCapture: (payload) =>
    api.post("/capture/classify", payload).then((r) => r.data),
};

export const TransactionAllocation = {
  match: (amount, reference, counterparty) =>
    api
      .post("/bills/match-transaction", { amount, reference, counterparty })
      .then((r) => r.data),
  splitAllocate: (data) =>
    api.post("/bills/split-allocate", data).then((r) => r.data),
};

export const Quote = {
  createIncomes: (data) =>
    api.post("/income/from-quote", data).then((r) => r.data),
};

export const INCOME_CATEGORIES = [
  "Install deposit 50%",
  "Install deposit 30%",
  "Install deposit 20%",
  "Service call",
  "Factory repair",
  "Other",
];

export const Income = {
  categories: () => api.get("/income/categories").then((r) => r.data),
  list: (params = {}) => api.get("/income", { params }).then((r) => r.data),
  create: (payload) => api.post("/income", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/income/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/income/${id}`).then((r) => r.data),
};

export const Settings = {
  getProfitGoal: () => api.get("/settings/profit-goal").then((r) => r.data),
  setProfitGoal: (amount) =>
    api.put("/settings/profit-goal", { amount }).then((r) => r.data),
};

export const Search = {
  query: (q, limit = 25) =>
    api.get("/search", { params: { q, limit } }).then((r) => r.data),
};

export const Jobs = {
  list: () => api.get("/jobs").then((r) => r.data),
  get: (tag) => api.get(`/jobs/${encodeURIComponent(tag)}`).then((r) => r.data),
};

export const Tags = {
  list: () => api.get("/tags").then((r) => r.data),
};

export const PnL = {
  data: (date_from, date_to) =>
    api.get("/pnl/data", { params: { date_from, date_to } }).then((r) => r.data),
};

export const Audit = {
  list: (params = {}) =>
    api.get("/audit-log", { params }).then((r) => r.data),
};

export const BankStatement = {
  parse: (payload) =>
    api.post("/bank-statement/parse", payload).then((r) => r.data),
};

export const Forecast = {
  threeMonth: () => api.get("/forecast/3-month").then((r) => r.data),
};

export const Receipts = {
  categories: () => api.get("/receipts/categories").then((r) => r.data),
  list: (params = {}) => api.get("/receipts", { params }).then((r) => r.data),
  get: (id) => api.get(`/receipts/${id}`).then((r) => r.data),
  create: (payload) => api.post("/receipts", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/receipts/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/receipts/${id}`).then((r) => r.data),
  stats: () => api.get("/receipts/stats").then((r) => r.data),
  ocr: (payload) =>
    api.post("/ocr/receipt-extract", payload).then((r) => r.data),
  exportData: (date_from, date_to) =>
    api
      .get("/receipts-export/data", { params: { date_from, date_to } })
      .then((r) => r.data),
  exportCsv: async (date_from, date_to) => {
    const res = await api.get("/receipts-export/csv", {
      params: { date_from, date_to },
      responseType: "blob",
    });
    const url = URL.createObjectURL(
      new Blob([res.data], { type: "text/csv" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `billy_receipts_${date_from}_to_${date_to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

export const RECEIPT_CATEGORIES = [
  "Materials",
  "Tools",
  "Fuel",
  "Vehicle",
  "Office",
  "Subcontractor",
  "Meals",
  "Other",
];

export const PAYMENT_METHODS = [
  "Card",
  "Cash",
  "EFT / Bank Transfer",
  "BPAY",
  "PayID",
  "Cheque",
  "Other",
];

export const Weekly = {
  summary: (week_start) =>
    api
      .get("/weekly-summary", { params: week_start ? { week_start } : {} })
      .then((r) => r.data),
  trend: (weeks = 8) =>
    api.get("/weekly-trend", { params: { weeks } }).then((r) => r.data),
  rangeSummary: (date_from, date_to) =>
    api
      .get("/range-summary", { params: { date_from, date_to } })
      .then((r) => r.data),
};

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const comma = result.indexOf(",");
      resolve({
        base64: comma >= 0 ? result.slice(comma + 1) : result,
        mime: file.type || "application/octet-stream",
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
